import React, { useState } from "react";
import { CiMail } from "react-icons/ci";
import { FaYoutube, FaFacebookF, FaMeetup } from "react-icons/fa";
import { postSubscribe } from "../api/subscribe";
import { Link } from "react-router-dom";
import { executeCaptcha } from "../utils/recaptcha";

export default function Footer() {

  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [message, setMessage] = useState("");

  const onSubscribe = async (e) => {
    e.preventDefault();
    setMessage("");

    // Honeypot: if filled, silently "succeed"
    if (company.trim()) {
      setStatus("success");
      setEmail("");
      return;
    }

    const value = email.trim();

    // Light client-side validation to avoid noisy requests
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (!emailOk) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }

    setStatus("sending");
    try {
      const captchaToken = await executeCaptcha("subscribe_form");
      await postSubscribe({
        email: value,
        company,
        captchaToken,
      });
      setStatus("success");
      setMessage("Thank you! You’re subscribed.");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(err.message || "Something went wrong. Please try again.");
    }
  };

  const isSending = status === "sending";

  return (
    <footer className="site-footer">
      <div className="container">
        {/* Brand & subscribe */}
        <div className="footer-col footer-brand">
          <div className="footer-logo">
            <img src="/assets/armchair.png" alt="Living Room Inquiries logo" />
            <span className="footer-brandname">Living Room Inquiries</span>
          </div>

          <p className="footer-text">
            Subscribe for upcoming Living Rooms and the latest videos.
          </p>

          <form className="footer-subscribe" onSubmit={onSubscribe} noValidate>
            <input
              className="field footer-input"
              type="email"
              placeholder="Email address"
              aria-label="Email address"
              aria-invalid={status === "error" ? "true" : "false"}
              aria-describedby="subscribe-status"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
            {/* Honeypot field (hidden from users) */}
            <input
              type="text"
              name="company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hp-field"
            />
            <button
              className="btn btn--green footer-button"
              type="submit"
              disabled={isSending}
            >
              {isSending ? "Subscribing…" : "Subscribe"}
            </button>
          </form>

          {/* Live region for success/error feedback */}
          <p
            id="subscribe-status"
            className={
              status === "success"
                ? "footer-success"
                : status === "error"
                ? "footer-error"
                : "footer-hint"
            }
            role="status"
            aria-live="polite"
          >
            {message}
          </p>
        </div>

        {/* Explore */}
        <nav className="footer-col" aria-label="Explore">
          <h3 className="footer-title">Explore</h3>
          <Link to="/">Home</Link>
          <Link to="/about">About</Link>
          <Link to="/events">Events</Link>
          <Link to="/videos">Videos</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/private-sessions">Private Sessions</Link>
        </nav>

        {/* Resources */}
        <nav className="footer-col" aria-label="Links">
          <h3 className="footer-title">External Links</h3>
          <a href="https://www.meetup.com/thework-runsfree/" target="_blank" rel="noopener noreferrer">The Work Runs Free</a>
          <a href="https://www.thework.com/" target="_blank" rel="noopener noreferrer">The Work of BK</a>
        </nav>

        {/* Connect */}
        <div className="footer-col">
          <h3 className="footer-title">Connect</h3>
          <div className="footer-info">
            <CiMail size={18} aria-hidden="true" />
            <a href="mailto:livingroominquiries@proton.me">
              livingroominquiries@proton.me
            </a>
          </div>
          <div className="footer-social">
            <a
              href="https://youtube.com/@LivingRoomInquiries"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className="footer-social-icon"
            >
              <FaYoutube size={22} />
            </a>
            <a
              href="https://facebook.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="footer-social-icon"
            >
              <FaFacebookF size={20} />
            </a>
            <a
              href="https://www.meetup.com/thework-runsfree/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Meetup"
              className="footer-social-icon"
            >
              <FaMeetup size={20} />
            </a>
          </div>
        </div>
      </div>
      <div className="footer-hr"></div>
      <div className="footer-bottom">
        <div className="footer-bottom-inner">
        <span>&copy; {new Date().getFullYear() } Living Room Inquiries | All Rights Reserved</span>
        <span>Website Designed & Developed by Helios Chan</span>
        </div>
      </div>
    </footer>
  );
}
